/**
 * lib/listingSchema.js — v2-listing-schema
 * ---------------------------------------------------------------
 * Central source of truth for the LISTING SCHEMA — the unified
 * configuration that drives BOTH the listing submission form AND
 * the public search filters. Designed so that:
 *
 *   • A single schema document defines `fields` and `filters`.
 *   • Each filter is LINKED to a form field via `linkedFieldKey`,
 *     so options never duplicate between the form and the filters.
 *   • A safe DEFAULT_LISTING_SCHEMA exists so the UI never breaks
 *     when the backend is unreachable, returns stale data, or the
 *     user is anonymous (admin endpoint 401).
 *   • Cache busting is version-controlled via LISTING_SCHEMA_VERSION
 *     so old production users automatically discard stale schema.
 *   • Future-proof: `schemaType` keyword reserved for the eventual
 *     "project" schema rebuild (PROJECT_SCHEMA_VERSION) without
 *     re-architecting this module.
 *
 * Module API
 * ──────────
 *   LISTING_SCHEMA_VERSION            — version constant
 *   DEFAULT_LISTING_SCHEMA            — hardcoded fallback schema
 *   normalizeListingSchema(raw)       — coerce arbitrary backend
 *                                       payloads into the canonical
 *                                       shape (fills missing defaults,
 *                                       drops unknown noise)
 *   fetchListingSchema(api, opts)     — admin-first, fall back to
 *                                       public-safe schema on 401
 *   getPublicSafeListingSchema(s)     — strip admin-only fields
 *   purgeStaleListingSchemaCache()    — wipe pre-version localStorage
 *   getFieldByKey(schema, key)
 *   getFormFieldsBySection(schema)    — { sectionName: [field, ...] }
 *   getFiltersFromSchema(schema, opts)
 *   getFieldOptions(schema, fieldKey)
 *
 * Backward compatibility
 * ──────────────────────
 * The legacy `/api/admin/filters` endpoint still returns the flat
 * filters_config list. `normalizeListingSchema` accepts EITHER the
 * new `{version, fields, filters}` envelope OR the legacy flat list
 * (auto-converts each row into the equivalent {field, filter} pair).
 */

export const LISTING_SCHEMA_VERSION = "v2-listing-schema";

// localStorage keys this module owns (purged on version mismatch)
export const SCHEMA_CACHE_KEY = "houseentry.listing_schema.cache";
export const SCHEMA_VERSION_KEY = "houseentry.listing_schema.version";

// Stale keys from earlier ad-hoc filter caches. purgeStaleListingSchemaCache
// drops these unconditionally so production users force a clean refetch.
const LEGACY_CACHE_KEYS = [
  "houseentry.filters.cache",
  "houseentry.config.cache",
  "filters_cache",
  "config_cache",
];

// ────────────────────────────────────────────────────────────────
// Canonical sections (form layout)
// ────────────────────────────────────────────────────────────────
export const FIELD_SECTIONS = [
  "Basic Details",
  "Pricing",
  "Location",
  "Property Details",
  "Amenities",
  "Media",
  "Owner/Contact",
  "Admin/Internal",
];

// ────────────────────────────────────────────────────────────────
// Canonical field types (form)
// ────────────────────────────────────────────────────────────────
export const FIELD_TYPES = [
  "text",
  "number",
  "textarea",
  "dropdown",
  "multi-select",
  "checkbox",
  "toggle",
  "range",
  "date",
  "floor-dropdown",
  "media",
  "location",
  "hidden",
];

// ────────────────────────────────────────────────────────────────
// Canonical filter UI types (search)
// ────────────────────────────────────────────────────────────────
export const FILTER_UI_TYPES = [
  "dropdown",
  "multi-select",
  "range",
  "checkbox",
  "toggle",
  "search",
  "location-select",
];

// Filter placement on the public listing page
export const FILTER_PLACEMENTS = ["main", "more", "hidden"];

// Canonical role keys (visibility / role_visibility)
export const ROLES = ["admin", "team", "dealer", "builder", "client", "public"];

// Canonical transaction types (form-level)
export const TRANSACTION_TYPES = ["Sale", "Rent", "Lease"];

// iter_110 — Display surfaces (independent from form-visibility).
//
// Layout-driven UIs (PropertyDetail, listing cards, CRM, PDF) consult
// these flags to decide whether they MAY render a field. Internal /
// sensitive fields (lat/lng, internal IDs, raw storage paths) set
// apiInternalOnly=true so they never leak to any UI surface.
export const DISPLAY_SURFACES = [
  "submissionForm",
  "editForm",
  "publicDetail",
  "adminDetail",
  "listingCard",
  "compare",
  "pdf",
  "crm",
  "exportable",
  "apiInternalOnly",
];

export const DISPLAY_SURFACE_LABELS = {
  submissionForm: "Submission Form",
  editForm: "Edit Form",
  publicDetail: "Public Property Detail",
  adminDetail: "Admin Property Detail",
  listingCard: "Listing Card",
  compare: "Compare",
  pdf: "PDF / Share Export",
  crm: "CRM",
  exportable: "CSV / Exportable",
  apiInternalOnly: "API Internal Only (never render)",
};

// Renderer hints — UI layouts pick the right component, never raw values.
export const RENDERER_HINTS = [
  "text", "textarea", "phone-tel", "money", "size", "distance",
  "tags", "toggle", "link-google-maps", "media-gallery",
  "approx-location", "internal-id", "hidden", "date",
];

// Default visibility — most fields are publicly visible. Internal
// fields override these defaults in seed.
export const DEFAULT_DISPLAY_VISIBILITY = {
  submissionForm: false,
  editForm: false,
  publicDetail: true,
  adminDetail: true,
  listingCard: false,
  compare: true,
  pdf: true,
  crm: true,
  exportable: true,
  apiInternalOnly: false,
};

// iter_111 — Detail-page DISPLAY ZONES (finer than displayVisibility).
// displayVisibility.publicDetail answers "may this render at all?".
// displayZones answers "WHERE in the layout?".
//
// Layouts (PropertyDetail) consult getDetailFields(schema, property, zone)
// which dedupes across zones so a single field never renders twice
// across the whole page.
export const DISPLAY_ZONES = [
  "hero",            // image gallery / cover
  "title",           // title + tagline block
  "pricingSummary",  // price, monthly_rent, commission (admin)
  "primarySpecs",    // top spec cards (BHK, Size, Floor, Furnishing, Parking)
  "locationCard",    // main_area / sub_area / landmark / google_maps_link
  "moreDetails",     // secondary key/value table at the bottom
  "card",            // listing-card preview tiles
  "compare",         // comparison table
  "form",            // listing submission / edit form
  "filter",          // search filter row
  "adminContact",    // admin-only contact block (separate from public)
  "internalOnly",    // never rendered (mirrors apiInternalOnly)
];

export const DISPLAY_ZONE_LABELS = {
  hero: "Hero (Media gallery)",
  title: "Title block",
  pricingSummary: "Pricing summary",
  primarySpecs: "Primary specs",
  locationCard: "Location card",
  moreDetails: "More Details (secondary)",
  card: "Listing card",
  compare: "Compare table",
  form: "Submission / Edit form",
  filter: "Search filter row",
  adminContact: "Admin-only contact",
  internalOnly: "Internal only (never render)",
};

// Default floor-dropdown options (used everywhere unless admin overrides)
export const DEFAULT_FLOOR_OPTIONS = [
  "Basement",
  "Lower Ground",
  "Ground",
  "Upper Ground",
  "1st Floor",
  "2nd Floor",
  "3rd Floor",
  "4th Floor",
  "5th Floor",
  "6th Floor",
  "7th Floor",
  "8th Floor",
  "9th Floor",
  "10th Floor",
  "11th Floor",
  "12th Floor",
  "Top Floor",
];

// ────────────────────────────────────────────────────────────────
// DEFAULT_LISTING_SCHEMA — hardcoded fallback (cannot rely on net)
// ────────────────────────────────────────────────────────────────
//
// This is the minimum schema the public UI needs to keep rendering
// when the backend is unreachable or returns an empty payload. It
// is deliberately conservative — admin can extend everything via
// the schema management UI without code changes.
//
// Each entry is normalised through normalizeListingSchema on read
// so adding/removing optional keys here is safe.
//
export const DEFAULT_LISTING_SCHEMA = {
  version: LISTING_SCHEMA_VERSION,
  schemaType: "listing",
  fields: [
    {
      key: "listing_intent",
      label: "Listing kis ke liye hai?",
      type: "dropdown",
      section: "Basic Details",
      required: true,
      active: true,
      locked: true,
      options: ["Sale", "Rent", "Lease"],
      sortOrder: 1,
      showOnForm: true,
    },
    {
      key: "listing_type",
      label: "Property Condition",
      type: "dropdown",
      section: "Basic Details",
      options: ["New", "Resale"],
      sortOrder: 2,
      showOnForm: true,
      transactionTypes: ["Sale"],
    },
    {
      key: "property_type",
      label: "Property Type",
      type: "dropdown",
      section: "Basic Details",
      required: true,
      locked: true,
      options: [
        "Builder Floor",
        "Independent House",
        "Plot",
        "Shop",
        "Office",
        "Building",
      ],
      sortOrder: 3,
      showOnForm: true,
    },
    {
      key: "bhk",
      label: "BHK / Size",
      type: "dropdown",
      section: "Property Details",
      options: ["1 BHK", "2 BHK", "3 BHK", "4 BHK", "5 BHK"],
      sortOrder: 10,
      showOnForm: true,
      propertyTypes: ["Builder Floor", "Independent House"],
    },
    {
      key: "furnishing",
      label: "Furnishing",
      type: "dropdown",
      section: "Property Details",
      options: ["Unfurnished", "Semi-Furnished", "Fully Furnished"],
      sortOrder: 11,
      showOnForm: true,
    },
    {
      key: "current_floor",
      label: "Current Floor",
      type: "floor-dropdown",
      section: "Property Details",
      options: DEFAULT_FLOOR_OPTIONS.slice(0, 16),
      sortOrder: 12,
      showOnForm: true,
      propertyTypes: ["Builder Floor", "Shop", "Office"],
    },
    {
      key: "total_floors",
      label: "Total Floors",
      type: "dropdown",
      section: "Property Details",
      options: [
        "Ground only", "G+1", "G+2", "G+3", "G+4", "G+5", "G+6", "G+7",
        "G+8", "G+9", "G+10", "G+11", "G+12", "G+13+",
      ],
      sortOrder: 13,
      showOnForm: true,
      propertyTypes: ["Builder Floor", "Independent House", "Building"],
    },
    {
      key: "main_area",
      label: "Main Area",
      type: "location",
      section: "Location",
      required: true,
      locked: true,
      sortOrder: 20,
      showOnForm: true,
    },
    {
      key: "sub_area",
      label: "Sub Area",
      type: "location",
      section: "Location",
      required: true,
      locked: true,
      sortOrder: 21,
      showOnForm: true,
    },
    {
      key: "price",
      label: "Price",
      type: "number",
      section: "Pricing",
      required: true,
      locked: true,
      sortOrder: 30,
      showOnForm: true,
    },
    {
      key: "metro_distance",
      label: "Metro Distance (m)",
      type: "range",
      section: "Location",
      sortOrder: 22,
      showOnForm: true,
      validation: { min: 0, max: 3000 },
    },
    {
      key: "tag",
      label: "Tag",
      type: "multi-select",
      section: "Admin/Internal",
      options: ["Verified", "Near Metro", "High ROI", "Premium Deal", "Best Deal"],
      sortOrder: 90,
      showOnForm: false, // admin-only — set via admin tools, not the public form
      roleVisibility: { admin: true, team: true, dealer: false, builder: false, public: false },
    },
    {
      key: "best_deal",
      label: "Best Deal",
      type: "toggle",
      section: "Admin/Internal",
      sortOrder: 91,
      showOnForm: false,
      roleVisibility: { admin: true, team: true, dealer: false, builder: false, public: false },
    },
    {
      key: "premium_deal",
      label: "Premium Listing",
      type: "toggle",
      section: "Admin/Internal",
      sortOrder: 92,
      showOnForm: false,
      roleVisibility: { admin: true, team: true, dealer: false, builder: false, public: false },
    },
  ],
  filters: [
    { key: "f_listing_type", linkedFieldKey: "listing_type", label: "Property Condition", uiType: "multi-select", placement: "more", sortOrder: 1 },
    { key: "f_property_type", linkedFieldKey: "property_type", label: "Property Type", uiType: "multi-select", placement: "main", sortOrder: 2 },
    { key: "f_bhk", linkedFieldKey: "bhk", label: "BHK", uiType: "multi-select", placement: "main", sortOrder: 3 },
    { key: "f_main_area", linkedFieldKey: "main_area", label: "Main Area", uiType: "multi-select", placement: "main", sortOrder: 4 },
    { key: "f_sub_area", linkedFieldKey: "sub_area", label: "Sub Area", uiType: "dropdown", placement: "main", sortOrder: 5 },
    { key: "f_furnishing", linkedFieldKey: "furnishing", label: "Furnishing", uiType: "dropdown", placement: "main", sortOrder: 6 },
    { key: "f_current_floor", linkedFieldKey: "current_floor", label: "Current Floor", uiType: "multi-select", placement: "more", sortOrder: 10 },
    { key: "f_total_floors", linkedFieldKey: "total_floors", label: "Total Floors", uiType: "multi-select", placement: "more", sortOrder: 11 },
    { key: "f_price", linkedFieldKey: "price", label: "Budget", uiType: "range", placement: "main", sortOrder: 7 },
    { key: "f_metro_distance", linkedFieldKey: "metro_distance", label: "Metro Distance", uiType: "range", placement: "more", sortOrder: 12 },
    { key: "f_tag", linkedFieldKey: "tag", label: "Tags", uiType: "multi-select", placement: "more", sortOrder: 20 },
    { key: "f_best_deal", linkedFieldKey: "best_deal", label: "Best Deal Only", uiType: "toggle", placement: "more", sortOrder: 21 },
    { key: "f_premium_deal", linkedFieldKey: "premium_deal", label: "Premium Only", uiType: "toggle", placement: "more", sortOrder: 22 },
  ],
};

// ────────────────────────────────────────────────────────────────
// normalizeListingSchema(raw)
// ────────────────────────────────────────────────────────────────
//
// Accepts ANY backend payload — old flat filter list, new envelope,
// or partial / malformed input — and returns a canonical schema.
// Missing fields fall through to DEFAULT_LISTING_SCHEMA defaults so
// the UI never crashes on a half-deployed backend.
//
const DEFAULT_ROLE_VISIBILITY = {
  admin: true,
  team: true,
  dealer: true,
  builder: true,
  client: true,
  public: true,
};

function normalizeField(raw, idx = 0) {
  if (!raw || !raw.key) return null;
  const type = FIELD_TYPES.includes(raw.type) ? raw.type : "text";
  return {
    key: String(raw.key),
    label: String(raw.label || raw.name || raw.key),
    type,
    section: raw.section || raw.field_section || "Property Details",
    required: !!raw.required,
    active: raw.active !== undefined ? !!raw.active : raw.status !== "inactive",
    locked: !!raw.locked,
    options: Array.isArray(raw.options) ? raw.options.filter(Boolean) : [],
    placeholder: raw.placeholder || "",
    helpText: raw.helpText || raw.help_text || "",
    defaultValue: raw.defaultValue ?? raw.default_value ?? null,
    sortOrder: Number.isFinite(raw.sortOrder) ? raw.sortOrder : (Number.isFinite(raw.sort_order) ? raw.sort_order : 100 + idx),
    showOnForm: raw.showOnForm !== undefined ? !!raw.showOnForm : !!raw.show_on_form,
    roleVisibility: { ...DEFAULT_ROLE_VISIBILITY, ...(raw.roleVisibility || raw.role_visibility || {}) },
    propertyTypes: Array.isArray(raw.propertyTypes || raw.applicable_property_types) ? (raw.propertyTypes || raw.applicable_property_types) : [],
    transactionTypes: Array.isArray(raw.transactionTypes || raw.transaction_types) ? (raw.transactionTypes || raw.transaction_types) : [],
    validation: raw.validation || raw.field_validation || {},
    // Truth-aware metadata (v2-listing-schema iter_109)
    core: !!raw.core,
    storageKey: raw.storageKey || raw.storage_key || "",
    aliases: Array.isArray(raw.aliases) ? raw.aliases.filter(Boolean) : [],
    usedInForm: !!(raw.usedInForm ?? raw.used_in_form),
    usedInEdit: !!(raw.usedInEdit ?? raw.used_in_edit),
    usedInFilters: !!(raw.usedInFilters ?? raw.used_in_filters),
    // iter_110 — Multi-surface display visibility + renderer hint
    displayVisibility: (
      (raw.displayVisibility?.apiInternalOnly || raw.display_visibility?.apiInternalOnly)
        ? {
            submissionForm: false, editForm: false, publicDetail: false,
            adminDetail: false, listingCard: false, compare: false,
            pdf: false, crm: false, exportable: false, apiInternalOnly: true,
          }
        : { ...DEFAULT_DISPLAY_VISIBILITY, ...(raw.displayVisibility || raw.display_visibility || {}) }
    ),
    displayRenderer: raw.displayRenderer || raw.display_renderer || "text",
    // iter_111 — Per-zone placement within publicDetail surface
    displayZones: (
      (raw.displayVisibility?.apiInternalOnly || raw.display_visibility?.apiInternalOnly || raw.api_internal_only)
        ? Object.fromEntries(DISPLAY_ZONES.map((z) => [z, z === "internalOnly"]))
        : { ...(raw.displayZones || raw.display_zones || {}) }
    ),
  };
}

function normalizeFilter(raw, idx = 0) {
  if (!raw || !raw.key) return null;
  const uiType = FILTER_UI_TYPES.includes(raw.uiType || raw.filter_ui_type) ? (raw.uiType || raw.filter_ui_type) : "dropdown";
  const placement = FILTER_PLACEMENTS.includes(raw.placement || raw.filter_placement) ? (raw.placement || raw.filter_placement) : "more";
  return {
    key: String(raw.key),
    linkedFieldKey: String(raw.linkedFieldKey || raw.linked_field_key || raw.key),
    label: String(raw.label || raw.filter_label || raw.name || raw.key),
    uiType,
    placement,
    active: raw.active !== undefined ? !!raw.active : raw.filter_active !== false,
    sortOrder: Number.isFinite(raw.sortOrder) ? raw.sortOrder : (Number.isFinite(raw.filter_sort_order) ? raw.filter_sort_order : 100 + idx),
    visibility: { ...DEFAULT_ROLE_VISIBILITY, ...(raw.visibility || raw.filter_visibility || {}) },
    mobileVisible: raw.mobileVisible !== undefined ? !!raw.mobileVisible : raw.mobile_visible !== false,
    desktopVisible: raw.desktopVisible !== undefined ? !!raw.desktopVisible : raw.desktop_visible !== false,
    defaultCollapsed: !!raw.defaultCollapsed || !!raw.default_collapsed,
  };
}

export function normalizeListingSchema(raw) {
  if (!raw) return { ...DEFAULT_LISTING_SCHEMA };

  // Legacy: a plain array of filters_config docs. Each doc represents
  // both a form field AND a filter (single source of options).
  if (Array.isArray(raw)) {
    const fields = raw.map((r, i) => normalizeField(r, i)).filter(Boolean);
    const filters = raw.map((r, i) => normalizeFilter({
      key: `f_${r.key}`,
      linkedFieldKey: r.key,
      label: r.name || r.label || r.key,
      uiType:
        r.type === "slider" ? "range" :
        r.type === "toggle" ? "toggle" :
        (r.options && r.options.length > 0 ? "multi-select" : "dropdown"),
      placement: "more",
      active: r.status === "active",
      sortOrder: r.sort_order ?? 100 + i,
    }, i)).filter(Boolean);
    return { version: LISTING_SCHEMA_VERSION, schemaType: "listing", fields, filters };
  }

  // New envelope
  const fields = (Array.isArray(raw.fields) ? raw.fields : [])
    .map((f, i) => normalizeField(f, i))
    .filter(Boolean);
  const filters = (Array.isArray(raw.filters) ? raw.filters : [])
    .map((f, i) => normalizeFilter(f, i))
    .filter(Boolean);

  // If completely empty, use defaults (defensive)
  if (fields.length === 0 && filters.length === 0) return { ...DEFAULT_LISTING_SCHEMA };
  return {
    version: raw.version || LISTING_SCHEMA_VERSION,
    schemaType: raw.schemaType || "listing",
    fields,
    filters,
  };
}

// ────────────────────────────────────────────────────────────────
// getPublicSafeListingSchema(schema, viewerRole)
// ────────────────────────────────────────────────────────────────
//
// Strip admin-only fields/filters before exposing schema to public
// viewers. Used both server-side (for the public endpoint) and as
// a client-side defence-in-depth.
//
export function getPublicSafeListingSchema(schema, viewerRole = "public") {
  const s = normalizeListingSchema(schema);
  const isRoleVisible = (rv) => rv && (rv[viewerRole] !== false);
  return {
    ...s,
    fields: s.fields.filter((f) => f.active && isRoleVisible(f.roleVisibility)),
    filters: s.filters.filter((f) => f.active && isRoleVisible(f.visibility)),
  };
}

// ────────────────────────────────────────────────────────────────
// fetchListingSchema(api, opts)
// ────────────────────────────────────────────────────────────────
//
// Strategy:
//   1. If `tryAdmin` AND we have a token, hit /api/admin/listing-schema.
//      On 401/403/404 silently fall back to public.
//   2. Always end with /api/listing-schema/public on failure of admin
//      (or first attempt for non-admin viewers).
//   3. Last resort: legacy `/api/admin/filters` (then normalize).
//   4. Final fallback: DEFAULT_LISTING_SCHEMA.
//
// Never throws. Returns `{ schema, source, error }` for diagnostics.
//
export async function fetchListingSchema(api, { tryAdmin = false } = {}) {
  // 1. Admin path
  if (tryAdmin) {
    try {
      const r = await api.get("/admin/listing-schema");
      if (r?.data) return { schema: normalizeListingSchema(r.data), source: "admin", error: null };
    } catch (err) {
      // 401/403/404 → silently degrade. Other errors still degrade.
    }
  }

  // 2. Public path
  try {
    const r = await api.get("/listing-schema/public");
    if (r?.data) return { schema: normalizeListingSchema(r.data), source: "public", error: null };
  } catch (err) {
    // continue to legacy fallback
  }

  // 3. Legacy /admin/filters (only useful if caller has admin token)
  if (tryAdmin) {
    try {
      const r = await api.get("/admin/filters");
      if (Array.isArray(r?.data)) {
        return { schema: normalizeListingSchema(r.data), source: "admin-legacy", error: null };
      }
    } catch (err) {
      // continue
    }
  }

  // 4. Hardcoded fallback — never throw.
  return { schema: { ...DEFAULT_LISTING_SCHEMA }, source: "default", error: "schema-unavailable" };
}

// ────────────────────────────────────────────────────────────────
// Cache management
// ────────────────────────────────────────────────────────────────

// Wipe pre-version localStorage caches so production users force a
// clean refetch instead of consuming stale data. Idempotent; safe to
// call at every app mount.
export function purgeStaleListingSchemaCache() {
  try {
    const storedVersion = localStorage.getItem(SCHEMA_VERSION_KEY);
    if (storedVersion !== LISTING_SCHEMA_VERSION) {
      localStorage.removeItem(SCHEMA_CACHE_KEY);
      LEGACY_CACHE_KEYS.forEach((k) => localStorage.removeItem(k));
      localStorage.setItem(SCHEMA_VERSION_KEY, LISTING_SCHEMA_VERSION);
    }
  } catch (_) {
    // localStorage unavailable (SSR / privacy mode) — silent.
  }
}

export function readCachedSchema() {
  try {
    const storedVersion = localStorage.getItem(SCHEMA_VERSION_KEY);
    if (storedVersion !== LISTING_SCHEMA_VERSION) return null;
    const raw = localStorage.getItem(SCHEMA_CACHE_KEY);
    if (!raw) return null;
    return normalizeListingSchema(JSON.parse(raw));
  } catch (_) {
    return null;
  }
}

export function writeCachedSchema(schema) {
  try {
    localStorage.setItem(SCHEMA_VERSION_KEY, LISTING_SCHEMA_VERSION);
    localStorage.setItem(SCHEMA_CACHE_KEY, JSON.stringify(schema));
  } catch (_) {
    // quota / privacy mode — silent.
  }
}

// ────────────────────────────────────────────────────────────────
// Selector helpers (used by FilterPanel + UnifiedSubmissionForm)
// ────────────────────────────────────────────────────────────────

export function getFieldByKey(schema, key) {
  const s = normalizeListingSchema(schema);
  return s.fields.find((f) => f.key === key) || null;
}

export function getFieldOptions(schema, fieldKey) {
  const f = getFieldByKey(schema, fieldKey);
  return f?.options || [];
}

export function getFormFieldsBySection(schema, opts = {}) {
  const s = normalizeListingSchema(schema);
  const { propertyType, transactionType, role } = opts;
  const grouped = {};
  for (const sec of FIELD_SECTIONS) grouped[sec] = [];
  // Use a "_Other" bucket for anything that came in with a non-canonical section.
  grouped._Other = [];
  const visible = s.fields
    .filter((f) => f.active && f.showOnForm)
    .filter((f) => !role || (f.roleVisibility?.[role] !== false))
    .filter((f) => !propertyType || f.propertyTypes.length === 0 || f.propertyTypes.some((p) => p.toLowerCase() === String(propertyType).toLowerCase()))
    .filter((f) => !transactionType || f.transactionTypes.length === 0 || f.transactionTypes.some((t) => t.toLowerCase() === String(transactionType).toLowerCase()))
    .sort((a, b) => a.sortOrder - b.sortOrder);
  for (const f of visible) {
    const sec = grouped[f.section] ? f.section : "_Other";
    grouped[sec].push(f);
  }
  return grouped;
}

export function getFiltersFromSchema(schema, opts = {}) {
  const s = normalizeListingSchema(schema);
  const { placement, role = "public", viewport } = opts;
  const fieldByKey = Object.fromEntries(s.fields.map((f) => [f.key, f]));
  return s.filters
    .filter((f) => f.active)
    .filter((f) => !placement || f.placement === placement)
    .filter((f) => !role || (f.visibility?.[role] !== false))
    .filter((f) => !viewport || (viewport === "mobile" ? f.mobileVisible : f.desktopVisible))
    .map((f) => {
      const field = fieldByKey[f.linkedFieldKey] || null;
      return {
        ...f,
        // Inherit options + label fallback from the linked field
        options: field?.options || [],
        // Defensive label fallback
        label: f.label || field?.label || f.key,
        // Pass through field type for UI rendering hints
        linkedField: field,
      };
    })
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

// ────────────────────────────────────────────────────────────────
// Alias resolution
// ────────────────────────────────────────────────────────────────
//
// Build a lookup of every alias → canonical key so that old DB
// records can be displayed via the new schema field. Example: an
// old record with `custom_fields.floor = "3"` resolves through the
// `current_floor` field's alias list and the form/UI shows the
// migrated value.
export function buildAliasMap(schema) {
  const s = normalizeListingSchema(schema);
  const map = new Map();
  for (const f of s.fields) {
    for (const a of (f.aliases || [])) {
      if (!map.has(a)) map.set(a, f.key);
    }
  }
  return map;
}

// Resolve any (key or alias) → canonical field key, or null if unknown.
export function resolveCanonicalKey(schema, anyKey) {
  const s = normalizeListingSchema(schema);
  const f = s.fields.find((x) => x.key === anyKey);
  if (f) return f.key;
  for (const x of s.fields) {
    if ((x.aliases || []).includes(anyKey)) return x.key;
  }
  return null;
}

// ────────────────────────────────────────────────────────────────
// canRender(field, surface) — single rule for "may UI render this?"
// ────────────────────────────────────────────────────────────────
//
// Rendering rules MUST be layout-driven: a layout calls canRender(field,
// "publicDetail") before deciding to display a field. apiInternalOnly
// is the master kill-switch — internal fields (lat/lng, IDs, raw paths)
// can never render anywhere.
//
// Usage:
//   const f = getFieldByKey(schema, "google_maps_link");
//   if (canRender(f, "publicDetail")) {
//     // render via f.displayRenderer hint, NOT via raw value
//   }
export function canRender(field, surface = "publicDetail") {
  if (!field || !field.active) return false;
  const dv = field.displayVisibility || DEFAULT_DISPLAY_VISIBILITY;
  if (dv.apiInternalOnly) return false;
  return !!dv[surface];
}

// Convenience: list every field a layout may render on a given surface
// (sorted by sortOrder, role-filtered for the viewer).
export function getRenderableFields(schema, opts = {}) {
  const { surface = "publicDetail", role = "public" } = opts;
  const s = normalizeListingSchema(schema);
  return s.fields
    .filter((f) => canRender(f, surface))
    .filter((f) => f.roleVisibility?.[role] !== false)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

// ────────────────────────────────────────────────────────────────
// getDetailFields(schema, property, zone) — dedupe-aware zone listing
// ────────────────────────────────────────────────────────────────
//
// Used by PropertyDetail (and other layout components) to list every
// schema field that:
//   1. is active
//   2. has displayZones[zone] = true
//   3. resolves to a non-empty value on the given property doc
//   4. is NOT internalOnly
//
// Each item carries:
//   { key, label, value, displayRenderer, storageKey, field }
// so the caller can pick the right component (gallery/button/text)
// without re-querying the schema.
//
// `property` may use canonical keys, aliases, or `custom_fields.*` —
// we resolve transparently. If multiple aliases collide on the same
// canonical key, the canonical key wins (no duplicate display).
export function getDetailFields(schema, property, zone = "moreDetails", opts = {}) {
  if (!property) return [];
  const s = normalizeListingSchema(schema);
  const { role = "public" } = opts;
  const seenKeys = new Set();
  const cf = property.custom_fields || {};
  const out = [];
  const sorted = [...s.fields].sort((a, b) => a.sortOrder - b.sortOrder);
  for (const f of sorted) {
    if (!f.active) continue;
    if (f.displayZones?.internalOnly) continue;
    if (!f.displayZones?.[zone]) continue;
    // Role filter — anon viewers don't see admin-only fields.
    if (f.roleVisibility?.[role] === false) continue;
    // Dedupe by canonical key + aliases — never render the same field
    // twice even if multiple alias rows resolve to the same value.
    if (seenKeys.has(f.key)) continue;
    for (const a of (f.aliases || [])) if (seenKeys.has(a)) continue;
    // Resolve the value — try storage_key path, then key, then aliases.
    let value = _resolveFieldValue(property, cf, f);
    if (value === null || value === undefined || value === "" ||
        (Array.isArray(value) && value.length === 0)) continue;
    seenKeys.add(f.key);
    for (const a of (f.aliases || [])) seenKeys.add(a);
    out.push({
      key: f.key,
      label: f.label,
      value,
      displayRenderer: f.displayRenderer || "text",
      storageKey: f.storageKey,
      field: f,
    });
  }
  return out;
}

// Pure helper — resolves a field's runtime value across storage_key,
// canonical key, custom_fields path, and aliases (back-compat).
function _resolveFieldValue(property, cf, f) {
  // 1. storage_key path
  const sk = f.storageKey || "";
  if (sk.startsWith("custom_fields.")) {
    const tail = sk.split(".", 2)[1];
    if (cf[tail] !== undefined && cf[tail] !== null && cf[tail] !== "") return cf[tail];
  } else if (sk && property[sk] !== undefined && property[sk] !== null && property[sk] !== "") {
    return property[sk];
  }
  // 2. canonical key on top-level + custom_fields
  if (property[f.key] !== undefined && property[f.key] !== null && property[f.key] !== "") return property[f.key];
  if (cf[f.key] !== undefined && cf[f.key] !== null && cf[f.key] !== "") return cf[f.key];
  // 3. legacy aliases
  for (const a of (f.aliases || [])) {
    if (property[a] !== undefined && property[a] !== null && property[a] !== "") return property[a];
    if (cf[a] !== undefined && cf[a] !== null && cf[a] !== "") return cf[a];
  }
  return null;
}
